import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CheckCircle, XCircle, Clock, Eye, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPayments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    fetchPayments();
  }, [filter]);

  const checkAdminAccess = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (!response.data.is_admin) {
        // Silently redirect non-admin users
        navigate('/');
      }
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/payments?status=${filter}`, {
        withCredentials: true
      });
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (paymentId) => {
    if (!window.confirm('Are you sure you want to approve this payment and activate the membership?')) {
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/admin/payments/${paymentId}/approve`,
        { admin_notes: adminNotes },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setSelectedPayment(null);
      setAdminNotes('');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (paymentId) => {
    if (!window.confirm('Are you sure you want to reject this payment?')) {
      return;
    }

    if (!adminNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/payments/${paymentId}/reject`,
        { admin_notes: adminNotes },
        { withCredentials: true }
      );
      
      toast.success('Payment rejected');
      setSelectedPayment(null);
      setAdminNotes('');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject payment');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300'
    };
    
    const icons = {
      pending: <Clock className="w-4 h-4" />,
      approved: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status]}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="py-8 px-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Approvals</h1>
          <p className="text-gray-600">Review and manage payment submissions</p>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <div className="flex gap-2 p-2">
            {['pending', 'approved', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                <span className="ml-2 text-xs">
                  ({payments.filter(p => p.payment_status === status).length})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Payments List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No {filter} payments found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <div key={payment.payment_id} className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{payment.user_name}</h3>
                      {getStatusBadge(payment.payment_status)}
                    </div>
                    <p className="text-sm text-gray-600">{payment.user_email}</p>
                  </div>
                  
                  {payment.payment_status === 'pending' && (
                    <button
                      onClick={() => setSelectedPayment(selectedPayment === payment.payment_id ? null : payment.payment_id)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-medium"
                    >
                      <Eye className="w-4 h-4 inline mr-2" />
                      Review
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Plan</p>
                    <p className="font-semibold text-gray-900 capitalize">Teacher {payment.plan}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Billing</p>
                    <p className="font-semibold text-gray-900 capitalize">{payment.billing_period}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="font-semibold text-gray-900">{payment.currency} {payment.amount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                    <p className="font-semibold text-gray-900">
                      {payment.payment_method === 'airtel_money' ? 'Airtel Money (DRC)' : 
                       payment.payment_method === 'cash' ? 'Cash (Lubumbashi)' : 
                       'UPI (India)'}
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Transaction ID</p>
                    <p className="font-mono text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded">{payment.transaction_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Submitted</p>
                    <p className="text-sm text-gray-900">{formatDate(payment.submission_date)}</p>
                  </div>
                </div>

                {payment.payment_screenshot_url && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Payment Screenshot</p>
                    <a
                      href={payment.payment_screenshot_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block"
                    >
                      <img
                        src={payment.payment_screenshot_url}
                        alt="Payment proof"
                        className="max-w-md h-auto border-2 border-gray-200 rounded-lg hover:border-blue-500 transition-all cursor-pointer"
                      />
                    </a>
                  </div>
                )}

                {payment.reviewed_by && (
                  <div className="bg-gray-50 rounded-lg p-4 mt-4">
                    <p className="text-xs text-gray-500 mb-1">Reviewed by</p>
                    <p className="text-sm font-semibold text-gray-900">{payment.reviewed_by}</p>
                    <p className="text-xs text-gray-600 mt-1">{formatDate(payment.reviewed_at)}</p>
                    {payment.admin_notes && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500 mb-1">Admin Notes</p>
                        <p className="text-sm text-gray-700">{payment.admin_notes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Review Panel */}
                {selectedPayment === payment.payment_id && payment.payment_status === 'pending' && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Review Payment</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Verify the payment details and screenshot before approving. The user's membership will be activated immediately upon approval.
                      </p>
                      
                      <textarea
                        value={adminNotes}
                        onChange={(e) => setAdminNotes(e.target.value)}
                        placeholder="Add notes (optional for approval, required for rejection)"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none mb-4"
                        rows="3"
                      />

                      <div className="flex gap-3">
                        <button
                          onClick={() => handleApprove(payment.payment_id)}
                          disabled={processing}
                          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-300 transition-all"
                        >
                          <CheckCircle className="w-5 h-5 inline mr-2" />
                          {processing ? 'Approving...' : 'Approve Payment'}
                        </button>
                        
                        <button
                          onClick={() => handleReject(payment.payment_id)}
                          disabled={processing}
                          className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 disabled:bg-gray-300 transition-all"
                        >
                          <XCircle className="w-5 h-5 inline mr-2" />
                          {processing ? 'Rejecting...' : 'Reject Payment'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}

export default AdminPayments;
